'use client'

import { CheckSquare, XCircle, Archive, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Content } from '../useContentData'

export interface ContentSelectionToolbarProps {
  // Selection state
  selectedContent: Set<string>;
  isSelectionMode: boolean;
  
  // Content data for calculations
  contents: Content[];
  paginatedContents: Content[];
  
  // Selection actions
  onToggleSelectAll: () => void;
  
  // Bulk operations
  onBulkCancelScheduled: () => Promise<void>;
  onBulkArchive?: () => Promise<void>;
  onBulkDelete?: () => Promise<void>;
}

export function ContentSelectionToolbar({
  selectedContent,
  isSelectionMode,
  contents,
  paginatedContents,
  onToggleSelectAll,
  onBulkCancelScheduled,
  onBulkArchive,
  onBulkDelete
}: ContentSelectionToolbarProps) {
  // Only show if in selection mode with items selected
  if (!isSelectionMode || selectedContent.size === 0) {
    return null;
  }

  // Calculate what actions are available based on selected content
  const selectedItems = contents.filter(c => selectedContent.has(c.id));
  const hasScheduledItems = selectedItems.some(c => c.is_scheduled === true || c.display_status === 'SCHEDULED');
  const hasArchivableItems = selectedItems.some(c => c.status !== 'ARCHIVED');
  const hasDeletableItems = selectedItems.some(c => c.status !== 'DELETED');
  
  // Only show toolbar if there are actions available
  if (!hasScheduledItems && !hasArchivableItems && !hasDeletableItems) {
    return null;
  }

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-blue-200 shadow-sm animate-in slide-in-from-top-2 duration-300">
      <div className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm">
                <CheckSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {selectedContent.size} {selectedContent.size === 1 ? 'item' : 'items'} selected
                </p>
                <p className="text-xs text-gray-600">
                  {contents.length} total in current filter
                </p>
              </div>
            </div>
            <div className="hidden sm:block h-8 w-px bg-gray-300" />
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleSelectAll}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              {selectedContent.size === paginatedContents.length ? 'Deselect All' : 'Select All on Page'}
            </Button>
          </div>
          <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 xs:gap-3">
            {hasScheduledItems && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkCancelScheduled}
                className="bg-white hover:bg-gray-50 border-gray-300 transition-colors text-xs sm:text-sm"
              >
                <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Cancel Scheduled ({selectedItems.filter(c => c.is_scheduled === true || c.display_status === 'SCHEDULED').length})</span>
                <span className="sm:hidden">Cancel ({selectedItems.filter(c => c.is_scheduled === true || c.display_status === 'SCHEDULED').length})</span>
              </Button>
            )}
            {hasArchivableItems && onBulkArchive && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkArchive}
                className="bg-white hover:bg-gray-50 border-gray-300 transition-colors text-xs sm:text-sm"
              >
                <Archive className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Archive ({selectedItems.filter(c => c.status !== 'ARCHIVED').length})</span>
                <span className="sm:hidden">Archive ({selectedItems.filter(c => c.status !== 'ARCHIVED').length})</span>
              </Button>
            )}
            {hasDeletableItems && onBulkDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkDelete}
                className="bg-white hover:bg-red-50 border-red-300 text-red-600 hover:text-red-700 hover:border-red-400 transition-colors text-xs sm:text-sm"
              >
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Delete ({selectedItems.filter(c => c.status !== 'DELETED').length})</span>
                <span className="sm:hidden">Delete ({selectedItems.filter(c => c.status !== 'DELETED').length})</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}